'use client';

import { useRouter } from 'next/navigation';

import { useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import classNames from 'classnames';

import { Spinner } from '@uikit';

import { useFeatureTogglesQuery, useHotOffersInfinityQuery } from '@query';

import { getHotOffersSearchParams, HotOffersSortValue } from '@api';

import { MIXPANEL_EVENT_NAMES } from '@shared/api/mixPanel';
import { HotOffer, HotOfferSortByFilter } from '@shared/api/types/hotOffers';
import { useAuth } from '@shared/common/providers/AuthProvider';
import { useTrackMixPanel } from '@shared/common/providers/MixPanelProvider';

import PageHeader from '@store:web/components/layout/PageHeader/PageHeader';
import HotOfferCardLite from '@store:web/components/pages/HotOffersPage/HotOfferCardLite';
import HotOffersFilters from '@store:web/components/pages/HotOffersPage/HotOffersFilters';

export interface HotOffersPageParams {
  searchParams?: {
    sort?: string;
  };
}

export default function HotOffersPage({ searchParams: initialSearchParams }: HotOffersPageParams) {
  const { user } = useAuth();

  useTrackMixPanel(MIXPANEL_EVENT_NAMES.HOT_OFFERS_PAGE_VIEW, {
    distinct_id: user?.id as number,
  });

  const router = useRouter();
  const [sortBy, setSortBy] = useState<HotOfferSortByFilter>(
    () => getHotOffersSearchParams(initialSearchParams).sort,
  );
  const { data: { binanceHotOffers } = {} } = useFeatureTogglesQuery();
  const {
    data: { pages = [] } = {},
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isError,
  } = useHotOffersInfinityQuery({
    sort: sortBy,
  });
  const { ref } = useInView({
    onChange: (inView) =>
      inView && !isError && !isLoading && !isFetchingNextPage && fetchNextPage(),
  });

  const handleSortChange = (value: HotOffersSortValue) => {
    const updatedSearchParams = new URLSearchParams({ sort: value });

    router.replace(`/hot-offers?${updatedSearchParams}`);

    setSortBy(value);
  };

  const hotOffers: HotOffer[] = useMemo(() => pages.flatMap((page) => page.data ?? []), [pages]);

  useEffect(() => {
    history.scrollRestoration = 'manual';
  }, []);

  return (
    <main className="w-full flex flex-grow flex-col items-center bg-neutral-50">
      <PageHeader page="hot-offers" />

      <div className="v2-container pt-1 w-full flex flex-col">
        {binanceHotOffers && <HotOffersFilters sortBy={sortBy} onSortByChange={handleSortChange} />}

        {!!hotOffers.length && (
          <div
            id="onboarding-hot-offers"
            className={classNames(`w-full grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3`)}
          >
            {hotOffers.map((hotOffer, i) => (
              <HotOfferCardLite key={hotOffer.id} imagePriority={i <= 2} offer={hotOffer} />
            ))}
          </div>
        )}
      </div>

      <div className="w-full flex flex-col flex-grow flex-shrink-0 items-center justify-center gap-2 py-6">
        {(isLoading || hasNextPage) && (
          <>
            {isError && (
              <>
                <span className="text-neutral-500">Couldn't load more offers</span>
                <button onClick={() => fetchNextPage()} className="button button-outline-secondary">
                  Try Again
                </button>
              </>
            )}

            {!isError && (
              // Key is added to trigger in view cb again if component is already in view
              <Spinner key={pages.length} ref={ref} />
            )}
          </>
        )}

        {!isLoading && !hasNextPage && (
          <span className="text-neutral-500">
            {isError && "Couldn't load more offers"}
            {!hasNextPage && !!pages.length && "That's all for now!"}
            {!hasNextPage && !isError && !pages.length && 'No offers available at the moment.'}
          </span>
        )}
      </div>
    </main>
  );
}
